/**
 * Store Finder — store-finder.js
 * Place in: assets/store-finder.js
 *
 * Depends on: Leaflet 1.9+, postcodes.io (free UK geocoding, no API key)
 */

'use strict';

// ── Registry so we can destroy + reinit on theme editor reloads ──
const storeFinderInstances = new Map();

class StoreFinder {
  constructor(sectionId, data) {
    this.sectionId = sectionId;
    this.data = data;
    this.stores = data.stores.map(s => ({ ...s })); // shallow clone

    this.el = document.getElementById(`store-finder-${sectionId}`);
    if (!this.el) return;

    this.mapEl = document.getElementById(`store-finder-map-${sectionId}`);
    this.listEl = this.el.querySelector('.store-finder__list');
    this.countEl = this.el.querySelector('.store-finder__list-count');

    // Search controls (may not exist if disabled in settings)
    this.searchInput = this.el.querySelector('.store-finder__search-input');
    this.searchBtn = this.el.querySelector('[data-search-btn]');
    this.searchClear = this.el.querySelector('.store-finder__search-clear');
    this.searchError = this.el.querySelector('.store-finder__search-error');

    this.map = null;
    this.markers = {}; // index → Leaflet marker
    this.userMarker = null;

    this._init();
  }

  async _init() {
    await this._geocodeStores();
    this._initMap();
    this._bindEvents();
    this._updateCount(this.stores.length);
  }

  // ── Geocoding ───────────────────────────────────────────────

  async _geocodeStores() {
    // Use lat/lng overrides where provided; batch-geocode the rest via postcodes.io
    const needsGeocode = this.stores.filter(
      s => !(parseFloat(s.lat) && parseFloat(s.lng)) && s.postcode
    );

    if (!needsGeocode.length) return;

    const postcodes = needsGeocode.map(s => s.postcode);

    try {
      const res = await fetch('https://api.postcodes.io/postcodes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ postcodes }),
      });

      if (!res.ok) throw new Error(`postcodes.io returned ${res.status}`);

      const { result } = await res.json();

      result.forEach((item, i) => {
        if (item && item.result) {
          needsGeocode[i].lat = item.result.latitude;
          needsGeocode[i].lng = item.result.longitude;
        }
      });
    } catch (err) {
      console.warn('[StoreFinder] Batch geocoding failed:', err);
    }
  }

  // ── Map init ────────────────────────────────────────────────

  _initMap() {
    if (!this.mapEl || !window.L) return;

    const tileConfig = this._tileConfig(this.data.mapTile);
    const validStores = this._validStores();

    // Default centre: middle of UK
    let center = [54.5, -3];
    const zoom = this.data.defaultZoom || 6;

    this.map = window.L.map(this.mapEl, {
      center,
      zoom,
      scrollWheelZoom: false,
      zoomControl: true,
    });

    window.L.tileLayer(tileConfig.url, {
      attribution: tileConfig.attribution,
      maxZoom: 19,
    }).addTo(this.map);

    validStores.forEach(store => this._addMarker(store));

    // Fit map to show all pins
    if (validStores.length > 1) {
      const bounds = window.L.latLngBounds(
        validStores.map(s => [parseFloat(s.lat), parseFloat(s.lng)])
      );
      this.map.fitBounds(bounds, { padding: [40, 40] });
    } else if (validStores.length === 1) {
      this.map.setView(
        [parseFloat(validStores[0].lat), parseFloat(validStores[0].lng)],
        12
      );
    }
  }

  _addMarker(store) {
    const latlng = [parseFloat(store.lat), parseFloat(store.lng)];
    const icon = this._markerIcon();

    const marker = window.L.marker(latlng, { icon });

    marker.bindPopup(this._popupHTML(store), {
      maxWidth: 260,
      className: 'store-finder__popup',
    });

    marker.on('click', () => {
      this._setActiveListItem(store.index);
    });

    marker.addTo(this.map);
    this.markers[store.index] = marker;
  }

  _markerIcon() {
    const color = this.data.markerColor || '#000000';
    // SVG pin: filled with theme color, white inner circle
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36" width="24" height="36">
      <path d="M12 0C5.373 0 0 5.373 0 12c0 9 12 24 12 24S24 21 24 12C24 5.373 18.627 0 12 0z" fill="${color}"/>
      <circle cx="12" cy="12" r="4.5" fill="white"/>
    </svg>`;

    return window.L.divIcon({
      html: svg,
      className: 'store-finder__marker',
      iconSize: [24, 36],
      iconAnchor: [12, 36],
      popupAnchor: [0, -38],
    });
  }

  _popupHTML(store) {
    const addressParts = [
      store.address_1,
      store.address_2,
      store.town,
      store.county,
      store.postcode,
    ].filter(Boolean);

    let html = `<div class="store-popup">`;
    html += `<strong class="store-popup__name heading h5">${this._esc(store.name)}</strong>`;

    if (addressParts.length) {
      html += `<p class="store-popup__address">${addressParts.map(p => this._esc(p)).join('<br>')}</p>`;
    }

    html += `<div class="store-popup__links">`;

    if (store.phone) {
      const tel = store.phone.replace(/\s/g, '');
      html += `<a href="tel:${this._esc(tel)}" class="store-popup__link">${this._esc(store.phone)}</a>`;
    }

    if (store.website) {
      const label = store.website
        .replace(/^https?:\/\/(www\.)?/, '')
        .replace(/\/$/, '');
      html += `<a href="${this._esc(store.website)}" target="_blank" rel="noopener noreferrer" class="store-popup__link">${this._esc(label)}</a>`;
    }

    if (store.instagram) {
      html += `<a href="https://instagram.com/${this._esc(store.instagram)}" target="_blank" rel="noopener noreferrer" class="store-popup__link">@${this._esc(store.instagram)}</a>`;
    }

    html += `</div></div>`;
    return html;
  }

  // ── Tile configs ────────────────────────────────────────────

  _tileConfig(tile) {
    const osm = '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> contributors';
    const carto = `${osm} &copy; <a href="https://carto.com/attributions" target="_blank">CARTO</a>`;

    const configs = {
      osm: {
        url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        attribution: osm,
      },
      carto_light: {
        url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
        attribution: carto,
      },
      carto_dark: {
        url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
        attribution: carto,
      },
      carto_voyager: {
        url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
        attribution: carto,
      },
    };

    return configs[tile] || configs.carto_light;
  }

  // ── Event binding ───────────────────────────────────────────

  _bindEvents() {
    // List item buttons → open popup + highlight
    this.el.querySelectorAll('.store-finder__list-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const index = parseInt(btn.dataset.index, 10);
        this._openMarker(index);
        this._setActiveListItem(index);

        // On mobile, scroll map into view
        if (window.innerWidth < 768) {
          this.mapEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });

    // Postcode search
    if (this.searchInput) {
      this.searchBtn.addEventListener('click', () => this._handleSearch());
      this.searchInput.addEventListener('keydown', e => {
        if (e.key === 'Enter') this._handleSearch();
      });
      this.searchClear.addEventListener('click', () => this._clearSearch());
    }
  }

  // ── Search & distance ───────────────────────────────────────

  async _handleSearch() {
    const raw = this.searchInput.value.trim();
    if (!raw) return;

    // Normalise: uppercase, strip internal spaces for the API call
    const postcode = raw.toUpperCase().replace(/\s+/g, '');

    this.searchError.hidden = true;
    this.searchBtn.disabled = true;
    this.searchBtn.textContent = '…';

    try {
      const res = await fetch(
        `https://api.postcodes.io/postcodes/${encodeURIComponent(postcode)}`
      );
      const json = await res.json();

      if (!res.ok || !json.result) {
        throw new Error('Not found');
      }

      const { latitude, longitude } = json.result;
      this._sortByDistance(latitude, longitude);
      this.searchClear.hidden = false;
    } catch {
      this.searchError.textContent = 'Please enter a valid UK postcode.';
      this.searchError.hidden = false;
    } finally {
      this.searchBtn.disabled = false;
      this.searchBtn.textContent = this.data.searchBtnText || 'Search';
    }
  }

  _sortByDistance(userLat, userLng) {
    const storesWithDist = this._validStores()
      .map(s => ({
        ...s,
        distance: this._haversine(
          userLat,
          userLng,
          parseFloat(s.lat),
          parseFloat(s.lng)
        ),
      }))
      .sort((a, b) => a.distance - b.distance);

    // Reorder DOM + show distance labels
    storesWithDist.forEach(store => {
      const item = this.listEl.querySelector(
        `.store-finder__list-item[data-index="${store.index}"]`
      );
      if (!item) return;

      this.listEl.appendChild(item); // moves to end → builds sorted order

      const distEl = item.querySelector('.store-finder__list-distance');
      distEl.textContent = `${store.distance.toFixed(1)} miles away`;
      distEl.hidden = false;
    });

    this._updateCount(storesWithDist.length, true);

    // Pan map and show user pin
    this.map.setView([userLat, userLng], 10, { animate: true });

    if (this.userMarker) this.map.removeLayer(this.userMarker);
    this.userMarker = window.L.circleMarker([userLat, userLng], {
      radius: 8,
      fillColor: '#4285f4',
      color: '#fff',
      weight: 2.5,
      fillOpacity: 1,
      className: 'store-finder__user-marker',
    })
      .addTo(this.map)
      .bindPopup('Your location');
  }

  _clearSearch() {
    this.searchInput.value = '';
    this.searchClear.hidden = true;
    this.searchError.hidden = true;

    if (this.userMarker) {
      this.map.removeLayer(this.userMarker);
      this.userMarker = null;
    }

    // Hide distance labels
    this.el
      .querySelectorAll('.store-finder__list-distance')
      .forEach(el => (el.hidden = true));

    // Restore original DOM order
    const items = [
      ...this.listEl.querySelectorAll('.store-finder__list-item'),
    ].sort(
      (a, b) =>
        parseInt(a.dataset.index, 10) - parseInt(b.dataset.index, 10)
    );
    items.forEach(item => this.listEl.appendChild(item));

    this._updateCount(this.stores.length);

    // Zoom back to fit all pins
    const validStores = this._validStores();
    if (validStores.length > 1) {
      const bounds = window.L.latLngBounds(
        validStores.map(s => [parseFloat(s.lat), parseFloat(s.lng)])
      );
      this.map.fitBounds(bounds, { padding: [40, 40], animate: true });
    }
  }

  // ── Helpers ─────────────────────────────────────────────────

  _openMarker(index) {
    const marker = this.markers[index];
    if (!marker) return;
    marker.openPopup();
    this.map.panTo(marker.getLatLng(), { animate: true });
  }

  _setActiveListItem(index) {
    this.el.querySelectorAll('.store-finder__list-item').forEach(item => {
      item.classList.toggle(
        'is-active',
        parseInt(item.dataset.index, 10) === index
      );
    });

    const activeItem = this.listEl.querySelector(
      `.store-finder__list-item[data-index="${index}"]`
    );
    if (activeItem) {
      activeItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  _updateCount(count, searched = false) {
    if (!this.countEl) return;
    const noun = `stockist${count !== 1 ? 's' : ''}`;
    this.countEl.textContent = searched
      ? `${count} ${noun} found`
      : `${count} ${noun}`;
  }

  _validStores() {
    return this.stores.filter(
      s => parseFloat(s.lat) && parseFloat(s.lng)
    );
  }

  /** Haversine distance in miles */
  _haversine(lat1, lng1, lat2, lng2) {
    const R = 3958.8;
    const toRad = d => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  /** Minimal HTML escaping for popup content */
  _esc(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  destroy() {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }
}

// ── Bootstrap ────────────────────────────────────────────────

function initAll() {
  document.querySelectorAll('[data-store-finder-section]').forEach(el => {
    const sectionId = el.dataset.storeFinderSection;
    if (!sectionId || el.dataset.sfInitialized) return;

    const dataEl = document.getElementById(`store-finder-data-${sectionId}`);
    if (!dataEl) return;

    let data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (err) {
      console.error('[StoreFinder] Could not parse section data:', err);
      return;
    }

    // Destroy any previous instance (theme editor reloads)
    if (storeFinderInstances.has(sectionId)) {
      storeFinderInstances.get(sectionId).destroy();
    }

    el.dataset.sfInitialized = 'true';
    const instance = new StoreFinder(sectionId, data);
    storeFinderInstances.set(sectionId, instance);
  });
}

/** Poll until Leaflet is available, then init */
function waitForLeaflet(callback, attempts = 0) {
  if (window.L) {
    callback();
  } else if (attempts < 60) {
    setTimeout(() => waitForLeaflet(callback, attempts + 1), 100);
  } else {
    console.warn('[StoreFinder] Leaflet did not load within 6 seconds.');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  waitForLeaflet(initAll);
});

// Shopify theme editor: reinit when section is reloaded
document.addEventListener('shopify:section:load', e => {
  const sectionEl = e.target.querySelector('[data-store-finder-section]');
  if (!sectionEl) return;
  delete sectionEl.dataset.sfInitialized;
  waitForLeaflet(initAll);
});
